import { useCallback, useEffect, useState } from "react";
import { api } from "../../lib/api";
import AdminShell from "../../components/AdminShell";
import { toast } from "sonner";
import { Loader2, AlertTriangle, CheckCircle2, Users } from "lucide-react";

/**
 * Model answer requests, grouped by question.
 *
 * Grouped because the question is the unit you release: ticking one row opens
 * that worked solution for everyone who asked for it, rather than making a
 * tutor approve the same question five times.
 *
 * Mark schemes never appear here. A student gets those the moment they hand in,
 * because that is how they mark their own work. A model answer is a full worked
 * solution, so it waits for a person.
 */
export default function AdminRequests() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [picked, setPicked] = useState([]);
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    api.get("/admin/answer-requests")
      .then((r) => { setRows(r.data || []); setPicked([]); })
      .catch((e) => toast.error(e.response?.data?.detail || "Could not load requests"))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const toggle = (code) =>
    setPicked((p) => (p.includes(code) ? p.filter((x) => x !== code) : [...p, code]));

  const release = (codes) => {
    if (codes.length === 0) return;
    setSaving(true);
    api.post("/admin/answer-requests/release", { question_codes: codes, kind: "model_answer" })
      .then((r) => { toast.success(`Released ${r.data.released} request(s)`); load(); })
      .catch((e) => toast.error(e.response?.data?.detail || "Could not release"))
      .finally(() => setSaving(false));
  };

  const flagged = rows.filter((r) => r.any_without_working).length;

  return (
    <AdminShell title="Answer requests" subtitle="Students asking to see a worked solution">
      {flagged > 0 && (
        <div className="mb-5 flex items-start gap-3 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3">
          <AlertTriangle size={18} className="mt-0.5 shrink-0 text-amber-600" />
          <p className="text-sm text-amber-900">
            <strong>{flagged} request{flagged === 1 ? "" : "s"} from someone who uploaded no working.</strong>{" "}
            Handing over a worked solution for a question they have not attempted teaches nothing — worth a
            conversation instead.
          </p>
        </div>
      )}

      <div className="flex items-center justify-between mb-4">
        <p className="text-sm text-slate-500">
          {loading ? "Loading…" : `${rows.length} question${rows.length === 1 ? "" : "s"} waiting`}
        </p>
        {rows.length > 0 && (
          <div className="flex gap-2">
            <button onClick={() => release(picked)} disabled={saving || picked.length === 0}
              className="bg-slate-800 text-white text-sm font-semibold rounded-lg px-3.5 py-2 disabled:opacity-40">
              Release {picked.length || ""} selected
            </button>
            <button onClick={() => release(rows.map((r) => r.question_code))} disabled={saving}
              className="border border-slate-300 text-slate-700 text-sm rounded-lg px-3.5 py-2 disabled:opacity-40">
              Release all {rows.length}
            </button>
          </div>
        )}
      </div>

      <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
        {loading && rows.length === 0 && (
          <p className="py-14 text-center text-sm text-slate-500 flex items-center justify-center gap-2">
            <Loader2 size={15} className="animate-spin" /> Loading…
          </p>
        )}
        {!loading && rows.length === 0 && (
          <div className="py-14 text-center">
            <CheckCircle2 size={28} className="mx-auto text-slate-300 mb-3" />
            <p className="font-semibold text-slate-600">Nothing waiting</p>
            <p className="text-sm text-slate-400 mt-1">Requests appear here after a student hands work in.</p>
          </div>
        )}

        {rows.map((r) => (
          <label key={`${r.question_code}-${r.kind}`}
            className="px-4 py-3.5 border-b border-slate-100 last:border-b-0 flex flex-wrap items-center gap-x-4 gap-y-2 hover:bg-slate-50/70 cursor-pointer">
            <input type="checkbox" checked={picked.includes(r.question_code)}
              onChange={() => toggle(r.question_code)} />
            <span className="font-mono text-sm font-semibold w-[84px]">{r.question_code}</span>
            <span className="flex-1 min-w-[150px] text-sm text-slate-600 inline-flex items-center gap-1.5">
              <Users size={13} className="text-slate-400" />
              {r.requested_by.join(", ")}
            </span>
            {r.any_without_working
              ? <span className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-full px-2.5 py-0.5 font-semibold">no working uploaded</span>
              : <span className="text-xs text-green-700 bg-green-50 border border-green-200 rounded-full px-2.5 py-0.5 font-semibold">attempted</span>}
          </label>
        ))}
      </div>
    </AdminShell>
  );
}
